/**
 * Outstanding background work — the runner's answer to "the turn ended but the
 * pod is still busy".
 *
 * A `run_in_background` Bash gate (or a backgrounded subagent) keeps running in
 * the pod after the agent's turn ends. The runner reported `idle` the instant
 * the turn finished, and an idle heartbeat does NOT bump
 * `Workspace.activityExpiresAt` — `heartbeatBumps`
 * (apps/api/src/services/workspace/activity-clock.ts) is `status !== "idle"` —
 * so the reconciler slept the pod at the activity window (default 5 min) and
 * killed the gate mid-run. This tracker is the mechanism that replaces the old
 * prompt-level advice ("the final gate must not use run_in_background"): while
 * it reports pending work the runner heartbeats non-idle and the clock stays
 * fresh.
 *
 * Two ends:
 *  - **Launch** is read off the turn's `tool_use` events. The harness stream
 *    has no background-task lifecycle event to prefer instead (`HarnessEvent`
 *    has no such member; the SDK's `task_started`/`task_progress` describe
 *    subagent progress *inside* a turn, not work that outlives it), so the
 *    tool input is the signal.
 *  - **Completion** is the CLI's synthetic `<task-notification>` user turn,
 *    read off the trusted transcript
 *    (`isBackgroundTaskNotificationRecord`, harness/pty/chat-record-mapper.ts).
 *
 * Correlating by task id is not possible from either end — the tool_use input
 * carries no id (the CLI mints it) and the notification text is prose — so the
 * tracker counts rather than pairs, and clamps at zero so a double
 * notification can never underflow into "pending forever".
 *
 * Safety cap: every entry carries an absolute deadline, so a notification that
 * never arrives (a lost record, the SDK harness, a child killed out from under
 * the CLI) cannot hold a pod awake indefinitely — the entry expires and the
 * runner falls back to reporting idle.
 */

/** How long one background launch may hold the runner non-idle. */
export const BACKGROUND_WORK_MAX_MS = 45 * 60 * 1000;

/**
 * Tools that can start work outliving the turn, and whether they do so by
 * default. `Bash` is foreground unless `run_in_background: true`; the subagent
 * tool is the inverse — it backgrounds unless told otherwise. `Task` is the
 * subagent tool's older name, accepted so the check survives a rename.
 */
const BACKGROUND_DEFAULT_BY_TOOL: Record<string, boolean> = {
  bash: false,
  agent: true,
  task: true,
};

function asRecord(input: unknown): Record<string, unknown> | null {
  if (typeof input === "string") {
    try {
      const parsed: unknown = JSON.parse(input);
      return typeof parsed === "object" && parsed !== null
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }
  return typeof input === "object" && input !== null ? (input as Record<string, unknown>) : null;
}

/**
 * Does this `tool_use` start work that outlives the turn? `input` arrives as
 * the JSON string the event pipeline serializes (event-handlers.ts), but a raw
 * object is accepted too.
 */
export function isBackgroundLaunch(tool: string, input: unknown): boolean {
  const byDefault = BACKGROUND_DEFAULT_BY_TOOL[tool.toLowerCase()];
  if (byDefault === undefined) return false;
  const flag = asRecord(input)?.run_in_background;
  return typeof flag === "boolean" ? flag : byDefault;
}

export interface BackgroundWorkTrackerOptions {
  /** Per-entry safety cap. Defaults to `BACKGROUND_WORK_MAX_MS`. */
  maxMs?: number;
  /** Fired whenever the pending set changes, so the caller can refresh the
   *  status it reports without waiting for the next heartbeat tick. */
  onChange?: () => void;
  /** Diagnostics sink (stderr in production). */
  log?: (message: string) => void;
}

/** Counts background launches that have not reported completion yet. */
export class BackgroundWorkTracker {
  /** Absolute expiry (epoch ms) of each outstanding launch, oldest first. */
  private readonly deadlines: number[] = [];
  private readonly maxMs: number;
  private readonly onChange: (() => void) | undefined;
  private readonly log: (message: string) => void;

  constructor(options: BackgroundWorkTrackerOptions = {}) {
    this.maxMs = options.maxMs ?? BACKGROUND_WORK_MAX_MS;
    this.onChange = options.onChange;
    this.log = options.log ?? ((message) => process.stderr.write(message));
  }

  /**
   * Feed one `tool_use` from the turn stream. Returns true when it registered
   * a background launch (test/diagnostic signal; callers can ignore it).
   */
  noteToolUse(tool: string, input: unknown, now: number = Date.now()): boolean {
    if (!isBackgroundLaunch(tool, input)) return false;
    this.deadlines.push(now + this.maxMs);
    this.log(
      `[conveyor-agent] background work started (${tool}); ` +
        `${this.deadlines.length} outstanding — heartbeats stay non-idle\n`,
    );
    this.onChange?.();
    return true;
  }

  /**
   * Feed one background-task completion notification. Clamped at zero: the
   * count is unpaired, so a notification with no matching launch (a launch the
   * runner never saw, a re-injected record) must be a no-op, never a negative
   * balance that would keep the pod awake after the real work finished.
   */
  noteCompletion(now: number = Date.now()): void {
    this.prune(now);
    if (this.deadlines.length === 0) return;
    // Oldest first: with no ids to pair on, FIFO is the best available guess
    // and keeps the surviving deadlines the ones with the most time left.
    this.deadlines.shift();
    this.log(
      `[conveyor-agent] background work finished; ${this.deadlines.length} outstanding\n`,
    );
    this.onChange?.();
  }

  /** Is any non-expired background work outstanding? */
  hasPending(now: number = Date.now()): boolean {
    this.prune(now);
    return this.deadlines.length > 0;
  }

  /** Outstanding, non-expired launches. */
  pendingCount(now: number = Date.now()): number {
    this.prune(now);
    return this.deadlines.length;
  }

  /** Drop everything (shutdown / session teardown). */
  clear(): void {
    if (this.deadlines.length === 0) return;
    this.deadlines.length = 0;
    this.onChange?.();
  }

  /** Expire entries past the safety cap, loudly — a tripped cap means a
   *  completion notification never arrived, which is worth seeing in the log. */
  private prune(now: number): void {
    let expired = 0;
    while (this.deadlines.length > 0 && this.deadlines[0] <= now) {
      this.deadlines.shift();
      expired++;
    }
    if (expired === 0) return;
    this.log(
      `[conveyor-agent] background work cap reached (${this.maxMs}ms) for ${expired} ` +
        `outstanding task(s) with no completion notification — reporting idle again\n`,
    );
    this.onChange?.();
  }
}
